import { BaseTexture, Rectangle, Texture } from "pixi.js"

import type { RgbaImage } from "game-common/room/autotile_compose"
import { seedTextureContentBoundsFromPixels } from "./texture_content_bounds"

const PAGE = 1024

/**
 * Art painted on the CPU (a wall cell with its trims, its grass fringe, its closed edges) packed onto shared canvas
 * pages, each page one base texture. A texture of its own each, a wall of distinct cells broke the batch at every
 * sixteenth texture and rebound slots all along it; in Chrome every one of those GL calls costs (~14us).
 *
 * Shelves of rows with a gap of one transparent pixel, so a nearest-sampled edge never picks up a neighbour. Each
 * piece's content bounds are measured from the pixels in hand (texture_content_bounds), so nothing reads it back from
 * the GPU. A page is still a canvas: what reads a wall's image (the damage holes, the bare edges) reads it through
 * the texture's frame, as from any atlas.
 */
export class CanvasAtlas {
    private pages: { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D; base: BaseTexture; x: number; y: number; shelf: number }[] = []

    constructor(private readonly size = PAGE) {}

    /** The image on a page; undefined when it is bigger than a page (or there is no 2D canvas), for the caller to keep its own. */
    put(image: RgbaImage): Texture | undefined {
        const place = this.place(image.width, image.height)
        if (!place) return undefined
        const data = place.page.context.createImageData(image.width, image.height)
        data.data.set(image.data)
        place.page.context.putImageData(data, place.x, place.y)
        return this.finish(place, image.data, image.width, image.height)
    }

    /** A canvas's picture on a page (its pixels read once, for its bounds). */
    putCanvas(canvas: HTMLCanvasElement): Texture | undefined {
        const context = canvas.getContext("2d")
        if (!context || !canvas.width || !canvas.height) return undefined
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data
        const place = this.place(canvas.width, canvas.height)
        if (!place) return undefined
        place.page.context.drawImage(canvas, place.x, place.y)
        return this.finish(place, pixels, canvas.width, canvas.height)
    }

    reset(): void {
        const old = this.pages
        this.pages = []
        // The sprites using them are gone with the full render; free the GPU copies a frame later.
        setTimeout(() => old.forEach(p => p.base.destroy()), 0)
    }

    get pageCount(): number {
        return this.pages.length
    }

    private place(width: number, height: number) {
        if (!width || !height || width > this.size || height > this.size) return undefined
        // A page someone destroyed (a texture freed with its base): its pieces are gone; start another.
        if (this.pages.length && this.pages[this.pages.length - 1].base.destroyed) this.pages.pop()
        let page = this.pages[this.pages.length - 1]
        const fits = (p: typeof page) => {
            if (p.x + width > this.size) {
                p.x = 0
                p.y += p.shelf
                p.shelf = 0
            }
            return p.y + height <= this.size
        }
        if (!page || !fits(page)) {
            const canvas = document.createElement("canvas")
            canvas.width = this.size
            canvas.height = this.size
            // In memory, not on the GPU: what reads a wall's image back (its damage, its edges) reads from this page.
            const context = canvas.getContext("2d", { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null
            if (!context) return undefined
            page = { canvas, context, base: BaseTexture.from(canvas), x: 0, y: 0, shelf: 0 }
            this.pages.push(page)
        }
        const at = { page, x: page.x, y: page.y }
        page.x += width + 1
        page.shelf = Math.max(page.shelf, height + 1)
        return at
    }

    private finish(place: { page: CanvasAtlas["pages"][number]; x: number; y: number }, pixels: ArrayLike<number>, width: number, height: number): Texture {
        place.page.base.update()
        const texture = new Texture(place.page.base, new Rectangle(place.x, place.y, width, height))
        seedTextureContentBoundsFromPixels(texture, pixels, width, height)
        return texture
    }
}

/** The CPU-painted wall pieces every map draws (closed edges, grass at the foot, joints): kept for the session. */
export const wallArtAtlas = new CanvasAtlas()
